import { Link } from 'react-router-dom';
import { DocSection, kbd } from '../../../pages/shell';

export const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'never-uploads', label: 'Your footage never uploads' },
  { id: 'internet-uses', label: 'The only times ClipRadar uses the internet' },
  { id: 'diagnostics', label: 'Diagnostics & crash reporting' },
  { id: 'deleting-data', label: 'Deleting your data' },
];

export default function Body() {
  return (
    <>
      <DocSection id="overview" title="Overview">
        <p>
          Editors work with unreleased footage, client material, and interviews that were never
          meant to leave a hard drive. ClipRadar is built around that: analysis and search both run
          entirely on your own machine, with no vector database and no cloud inference anywhere in
          the loop. This page is the exact, short list of the few times ClipRadar does reach the
          internet — and what it sends when it does.
        </p>
      </DocSection>

      <DocSection id="never-uploads" title="Your footage never uploads">
        <p>
          Indexing a source reads the file locally, extracts frames and audio, and writes what it
          finds into ClipRadar's own cache on your disk. Searching reads back from that same local
          cache. At no point in either step does a video frame, an audio clip, a filename, or a
          transcript line get sent anywhere — your footage never leaves the machine it was analyzed
          on.
        </p>
      </DocSection>

      <DocSection id="internet-uses" title="The only times ClipRadar uses the internet">
        <p>
          Outside of that, ClipRadar goes online for exactly five reasons, and never quietly beyond
          what each one needs:
        </p>
        <ol>
          <li>
            <strong>License activation &amp; check-ins</strong> — validating your key, and the
            periodic re-checks that back the offline grace period. See{' '}
            <Link to="/docs/concepts/licensing">Licensing &amp; activation</Link>.
          </li>
          <li><strong>Update checks</strong> — the desktop app checking for a newer build.</li>
          <li>
            <strong>Model downloads</strong> — fetching the Visual, Speech, Faces, and Ultra models
            on first run, or when you pick a different one, from their public model hosts.
          </li>
          <li>
            <strong>Bug reports you send</strong> — only when you explicitly submit one from Help →
            Report a bug.
          </li>
          <li><strong>Optional crash reports</strong> — only if you've turned diagnostics on.</li>
        </ol>
        <p>
          Nothing else calls out on its own. Clicking a link like Docs, Discord, or the pricing page
          opens your browser the way any link would — that's you leaving, not ClipRadar reporting
          anything.
        </p>
      </DocSection>

      <DocSection id="diagnostics" title="Diagnostics & crash reporting">
        <p>
          Settings → Privacy &amp; diagnostics has two independent switches.{' '}
          <span style={kbd}>Send crash reports &amp; diagnostics</span> controls whether crash
          reporting is armed at all, and whether a bug report you submit can attach recent logs —
          either way, what's ever collected is scrubbed of your footage, file contents, names, and
          paths first. It takes effect the next time you restart ClipRadar.{' '}
          <span style={kbd}>"Report an issue?" prompts</span> is separate: it only
          controls whether ClipRadar proactively offers to file a report after something goes wrong
          — a repeated empty search, a failed analysis, an Ultra fallback. Turning it off just stops
          the prompts; it doesn't touch the bug reporter itself.
        </p>
        <p>
          When you do submit a bug report, a description (and an optional email) always go out. If{' '}
          <strong>Include diagnostics</strong> is checked, it also attaches your app version, OS,
          GPU, and a handful of recent log lines — scrubbed of usernames and file paths on your
          machine before it's sent, then scrubbed again on arrival. Your footage is never part of a
          bug report.
        </p>
      </DocSection>

      <DocSection id="deleting-data" title="Deleting your data">
        <p>
          <strong>Delete from Library…</strong> in the Media tab's row menu clears ClipRadar's
          analysis of a source — it never touches your original file, which stays exactly where it
          is on disk. To clear ClipRadar's cache in bulk, or to find exactly where everything lives,
          see <Link to="/docs/reference/where-your-data-lives">Where ClipRadar stores things</Link>.
          To remove your license from a machine, use <strong>Deactivate</strong> under Settings →
          Licensing. For a full removal — including your cache and downloaded models — see{' '}
          <Link to="/docs/guides/uninstall">Uninstall ClipRadar</Link>.
        </p>
      </DocSection>
    </>
  );
}
